/**
 * DataGateway — roteia operações de dados entre o servidor LOCAL da loja
 * (altusfood-local-server, Fase 1) e o Supabase (nuvem), com fallback automático.
 *
 * Princípio (Fase 2):
 *  1. Tenta o servidor local com timeout curto.
 *  2. Sucesso  -> usa a resposta local (source: 'local').
 *  3. Erro de CONEXÃO (timeout, ECONNREFUSED, fetch failed) -> cai para Supabase (source: 'cloud').
 *  4. Erro de NEGÓCIO vindo da API local (HTTP 400/404/409...) -> NÃO faz fallback; propaga.
 *
 * IMPORTANTE: este gateway entra apenas na posição "chamada de rede online". Ele NÃO
 * substitui as camadas offline existentes (SQLite/IndexedDB/realtime) — os hooks continuam
 * decidindo, via getIsOnline()/navigator.onLine, quando usar cache offline. Quando o hook
 * decide ir à rede, é aqui que ele passa.
 */
import { supabase } from "@/integrations/supabase/client";

// Runtime-config (por dispositivo): a URL e a chave podem ser sobrescritas via tela de Ajustes,
// gravadas em localStorage. Necessário porque o build do Lovable é ÚNICO para todas as lojas —
// as VITE_* seriam globais. Cadeia de leitura: localStorage → VITE_* → default localhost:4477.
const LS_URL_KEY = "altus.localServer.url";
const LS_APIKEY_KEY = "altus.localServer.apiKey";
const DEFAULT_LOCAL_SERVER_URL = "http://localhost:4477";

/** URL efetiva do servidor local (lida a cada chamada → mudanças na tela valem na hora). */
export function getLocalServerUrl(): string {
  try {
    const v = localStorage.getItem(LS_URL_KEY);
    if (v && v.trim()) return v.trim();
  } catch { /* localStorage indisponível */ }
  return (import.meta.env.VITE_LOCAL_SERVER_URL as string | undefined) || DEFAULT_LOCAL_SERVER_URL;
}

/** Chave X-API-Key efetiva. Ausente → nenhum header (retrocompat com servidor sem auth). */
export function getLocalApiKey(): string | undefined {
  try {
    const v = localStorage.getItem(LS_APIKEY_KEY);
    if (v && v.trim()) return v.trim();
  } catch { /* idem */ }
  return import.meta.env.VITE_LOCAL_API_KEY as string | undefined;
}

/** Overrides atualmente salvos (null = usando o padrão do build). Para preencher a tela. */
export function getLocalServerOverrides(): { url: string | null; apiKey: string | null } {
  try {
    return { url: localStorage.getItem(LS_URL_KEY), apiKey: localStorage.getItem(LS_APIKEY_KEY) };
  } catch {
    return { url: null, apiKey: null };
  }
}

/** Grava (ou limpa, com null/'') os overrides por dispositivo. Usado pela tela de Ajustes. */
export function setLocalServerOverrides(url: string | null, apiKey: string | null): void {
  try {
    if (url && url.trim()) localStorage.setItem(LS_URL_KEY, url.trim());
    else localStorage.removeItem(LS_URL_KEY);
    if (apiKey && apiKey.trim()) localStorage.setItem(LS_APIKEY_KEY, apiKey.trim());
    else localStorage.removeItem(LS_APIKEY_KEY);
  } catch { /* ignore */ }
}

const LOCAL_TIMEOUT_MS = 1500;

// Circuit breaker: ao detectar o servidor local indisponível, pula direto para a nuvem
// durante esta janela, evitando esperar o timeout de 1.5s em toda chamada subsequente.
const LOCAL_DOWN_WINDOW_MS = 5000;
let localDownUntil = 0;

export type DataSource = "local" | "cloud";

export interface DataGatewayResult<T> {
  data: T | null;
  error: unknown;
  source: DataSource;
}

interface BusinessError {
  isBusinessError: true;
  status: number;
  body: unknown;
}

function isBusinessError(e: unknown): e is BusinessError {
  return typeof e === "object" && e !== null && (e as BusinessError).isBusinessError === true;
}

function localLikelyDown(): boolean {
  return Date.now() < localDownUntil;
}

const HEALTH_PROBE_TIMEOUT_MS = 800;

/**
 * Probe rápido: o servidor LOCAL está alcançável E SAUDÁVEL? Usado pelos guards de ESCRITA do
 * useOrders para, quando a INTERNET caiu mas a LAN está ok (servidor local de pé), seguir pelo
 * gateway (servidor local) em vez de cair para o SQLite do browser. Reutiliza o circuit breaker
 * `localLikelyDown()` (5s): se o local falhou há pouco, retorna false sem esperar o timeout.
 * Exige HTTP 200: o /health é honesto (503 quando o Postgres local está fora), então um servidor
 * de pé mas com o banco morto conta como INDISPONÍVEL → o PDV cai para a nuvem (comportamento
 * correto), em vez de gravar num local que não persiste.
 */
export async function isLocalServerAvailable(): Promise<boolean> {
  if (localLikelyDown()) return false;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HEALTH_PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${getLocalServerUrl()}/health`, { signal: controller.signal });
    if (!res.ok) {
      markLocalDown(); // 503 = banco fora → local indisponível
      return false;
    }
    markLocalUp();
    return true;
  } catch {
    markLocalDown();
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

function markLocalDown(): void {
  localDownUntil = Date.now() + LOCAL_DOWN_WINDOW_MS;
}

function markLocalUp(): void {
  localDownUntil = 0;
}

/**
 * Faz uma requisição ao servidor local. Lança:
 *  - BusinessError (isBusinessError) se a API local responder com HTTP 4xx (erro de negócio:
 *    validação/constraint/conflito) — NÃO faz fallback, propaga;
 *  - erro de conexão (qualquer outra coisa) se não der para falar com o servidor OU se o local
 *    responder 5xx. Um 5xx = o servidor está de pé mas não consegue cumprir (ex.: Postgres fora),
 *    o que é equivalente a "local indisponível" → os callers abrem o circuito e caem para a nuvem.
 */
async function tryLocal<T>(path: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), LOCAL_TIMEOUT_MS);
  const apiKey = getLocalApiKey();
  try {
    const res = await fetch(`${getLocalServerUrl()}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(apiKey ? { "X-API-Key": apiKey } : {}),
        ...(options?.headers || {}),
      },
    });
    if (res.status >= 500) {
      // servidor de pé, mas falhou (banco fora/erro interno) → trata como conexão indisponível
      throw new Error(`local server ${res.status}`);
    }
    if (res.status === 401) {
      // Erro de CONFIGURAÇÃO (X-API-Key ausente/incorreta), não de negócio nem de conexão.
      // Loga claro e propaga como businessError → o gateway NÃO cai para a nuvem: o operador
      // precisa perceber que a chave está errada, senão o local-first fica desligado sem aviso.
      console.error("[gateway] 401 do servidor local — X-API-Key ausente/incorreta");
      const body = await res.json().catch(() => ({}));
      const err: BusinessError = { isBusinessError: true, status: 401, body };
      throw err;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err: BusinessError = { isBusinessError: true, status: res.status, body };
      throw err;
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } finally {
    clearTimeout(timeout);
  }
}

// --- Indicador de origem (apenas para debug em dev) -------------------------
let lastSource: DataSource | null = null;
export function getLastDataSource(): DataSource | null {
  return lastSource;
}
function emitSource(source: DataSource): DataSource {
  lastSource = source;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("datagateway-source", { detail: source }));
  }
  return source;
}

/**
 * Executa uma leitura: tenta local, cai para a função de nuvem em erro de conexão.
 * `cloudCall` deve devolver o mesmo shape do Supabase: { data, error }.
 */
async function readWithFallback<T>(
  localPath: string,
  cloudCall: () => Promise<{ data: T | null; error: unknown }>,
  transformLocal: (raw: unknown) => T
): Promise<DataGatewayResult<T>> {
  if (!localLikelyDown()) {
    try {
      const raw = await tryLocal<unknown>(localPath);
      markLocalUp();
      return { data: transformLocal(raw), error: null, source: emitSource("local") };
    } catch (e) {
      if (isBusinessError(e)) {
        return { data: null, error: e.body, source: emitSource("local") };
      }
      markLocalDown(); // erro de conexão -> abre o circuito e cai para a nuvem
    }
  }
  const { data, error } = await cloudCall();
  return { data, error, source: emitSource("cloud") };
}

// ============================================================================
// Funções por entidade. Mantêm o shape { data, error } do Supabase + `source`.
// ============================================================================

/**
 * payment_methods — leitura. (Catálogo é somente-leitura no servidor local na Fase 1;
 * create/update/delete continuam indo direto ao Supabase nos hooks.)
 */
export async function getPaymentMethods<T = unknown>(
  restaurantId: string
): Promise<DataGatewayResult<T[]>> {
  return readWithFallback<T[]>(
    `/api/payment-methods?restaurant_id=${encodeURIComponent(restaurantId)}`,
    () =>
      supabase
        .from("payment_methods")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .order("is_system_default", { ascending: false })
        .order("name") as unknown as Promise<{ data: T[] | null; error: unknown }>,
    // Ordena o resultado local para casar com a ordenação da nuvem
    (raw) =>
      sortPaymentMethods(Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []) as T[]
  );
}

/**
 * Resultado de uma ESCRITA via gateway. 'ok' = servidor local gravou (a nuvem recebe via sync);
 * 'unavailable' = local inalcançável → o hook deve cair para o caminho de nuvem atual;
 * 'businessError' = 4xx do local → propagar sem fallback.
 */
export type WriteResult =
  | { kind: "ok"; data: unknown }
  | { kind: "unavailable" }
  | { kind: "businessError"; error: unknown };

async function writeLocal(path: string, body: unknown): Promise<WriteResult> {
  if (localLikelyDown()) return { kind: "unavailable" };
  try {
    const data = await tryLocal<unknown>(path, { method: "POST", body: JSON.stringify(body) });
    markLocalUp();
    emitSource("local");
    return { kind: "ok", data };
  } catch (e) {
    if (isBusinessError(e)) return { kind: "businessError", error: e.body };
    markLocalDown();
    return { kind: "unavailable" };
  }
}

/**
 * Fecha o pedido no servidor local (núcleo: campos financeiros + status + pagamentos + release
 * de mesa/comanda). A nuvem recebe via sync. O `command_entries` close e a limpeza do SQLite
 * continuam no hook (cloud-direct), fora deste gateway.
 */
export function closeOrderLocal(orderId: string, body: unknown): Promise<WriteResult> {
  return writeLocal(`/api/orders/${encodeURIComponent(orderId)}/close`, body);
}

/** Dados retornados pelo send-items local (mesmo shape do RPC send_order_items_fast). */
export interface SendItemsData {
  order_id: string;
  order_number: number;
  is_new_order: boolean;
  items_count: number;
}

/**
 * Lança itens no pedido (resolve-ou-cria, ocupa mesa/comanda, insere itens, recalcula totais)
 * no servidor local — equivalente ATÔMICO ao RPC send_order_items_fast. A nuvem recebe via sync.
 * 'ok' → usa data; 'unavailable' → o hook cai para o RPC da nuvem; 'businessError' → idem (o
 * local fez ROLLBACK, então não há duplo-write — a nuvem reavalia).
 */
export function sendItemsLocal(body: unknown): Promise<WriteResult> {
  return writeLocal("/api/orders/send-items", body);
}

/**
 * Cancela o item INTEIRO no servidor local (INSERT cancelamento + DELETE item + recalcula
 * totais, atômico). A nuvem recebe via sync. 'ok' → o hook faz early-return; 'unavailable' |
 * 'businessError' (rolled back) → cai para o caminho de nuvem atual (sem duplo-cancelamento).
 */
export function cancelItemLocal(itemId: string, body: unknown): Promise<WriteResult> {
  return writeLocal(`/api/order-items/${encodeURIComponent(itemId)}/cancel`, body);
}

/**
 * Cancelamento PARCIAL (decrease) no servidor local (INSERT cancelamento parcial + UPDATE
 * quantidade/total do item + recalcula totais, atômico). A nuvem recebe via sync. Mesma
 * semântica de fallback do cancelItemLocal.
 */
export function decreaseItemLocal(itemId: string, body: unknown): Promise<WriteResult> {
  return writeLocal(`/api/order-items/${encodeURIComponent(itemId)}/decrease`, body);
}

/**
 * Abre o caixa (período operacional) no servidor local — resolve-or-create. A nuvem recebe via
 * sync. Permite abrir caixa OFFLINE: sem um período aberto no local, o trigger require_open_period
 * barra a criação de pedido novo no PDV. 'ok' → usa data.period; 'unavailable' → o hook cai para
 * o cloud-direct (se online); 'businessError' (409 data já usada) → propaga sem fallback.
 */
export function openPeriodLocal(body: unknown): Promise<WriteResult> {
  return writeLocal("/api/operational-periods/open", body);
}

/**
 * Pedido atual de uma mesa/comanda (servidor local). Usado pelo useCurrentOrderItems como
 * FAST-PATH: assume só o caso 'order' (pedido ativo). 'none'/'unavailable' → o hook cai para a
 * lógica de nuvem inteira (com as guardas); 'businessError' → propaga sem fallback.
 */
export type CurrentOrderResult<O> =
  | { kind: "order"; order: O } // pedido ativo + items + cancellations
  | { kind: "none" } // local respondeu: sem pedido ativo
  | { kind: "unavailable" } // local inalcançável / erro de rede
  | { kind: "businessError"; error: unknown }; // 4xx do local

export async function getCurrentOrder<O = unknown>(
  resource: "tables" | "commands",
  id: string
): Promise<CurrentOrderResult<O>> {
  if (localLikelyDown()) return { kind: "unavailable" };
  try {
    const raw = await tryLocal<{ order: O | null }>(
      `/api/${resource}/${encodeURIComponent(id)}/current-order`
    );
    markLocalUp();
    emitSource("local");
    return raw.order ? { kind: "order", order: raw.order } : { kind: "none" };
  } catch (e) {
    if (isBusinessError(e)) return { kind: "businessError", error: e.body };
    markLocalDown();
    return { kind: "unavailable" };
  }
}

function sortPaymentMethods(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...rows].sort((a, b) => {
    const sysA = a.is_system_default ? 1 : 0;
    const sysB = b.is_system_default ? 1 : 0;
    if (sysA !== sysB) return sysB - sysA; // system_default primeiro
    return String(a.name ?? "").localeCompare(String(b.name ?? ""));
  });
}

/**
 * categories — leitura. (Catálogo é somente-leitura no servidor local na Fase 1;
 * create/update/delete/reorder continuam indo direto ao Supabase nos hooks.)
 */
export async function getCategories<T = unknown>(
  restaurantId: string
): Promise<DataGatewayResult<T[]>> {
  return readWithFallback<T[]>(
    `/api/categories?restaurant_id=${encodeURIComponent(restaurantId)}`,
    () =>
      supabase
        .from("categories")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .order("sort_order", { ascending: true }) as unknown as Promise<{
        data: T[] | null;
        error: unknown;
      }>,
    (raw) =>
      sortBySortOrder(
        Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
      ) as T[]
  );
}

function sortBySortOrder(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...rows].sort((a, b) => {
    const soA = Number(a.sort_order ?? 0);
    const soB = Number(b.sort_order ?? 0);
    if (soA !== soB) return soA - soB; // sort_order ascendente (igual à nuvem)
    return String(a.name ?? "").localeCompare(String(b.name ?? ""));
  });
}

/**
 * tables — leitura. O hook (useTables) ainda decide cache offline (SQLite/IndexedDB) e
 * realtime; aqui só roteamos a "chamada de rede online" local->nuvem. O hook re-ordena
 * com sortByNumberField, então devolvemos as linhas como vieram.
 */
export async function getTables<T = unknown>(
  restaurantId: string
): Promise<DataGatewayResult<T[]>> {
  return readWithFallback<T[]>(
    `/api/tables?restaurant_id=${encodeURIComponent(restaurantId)}`,
    () =>
      supabase
        .from("tables")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .order("number") as unknown as Promise<{ data: T[] | null; error: unknown }>,
    (raw) => (Array.isArray(raw) ? (raw as T[]) : ([] as T[]))
  );
}

/**
 * products — leitura. A API local embute `categories: { name }` e devolve
 * `production_locations: null` (impressão = Fase 5), casando com o shape do Supabase
 * (`select "*, categories(name), production_locations(name, printer_name)"`).
 * Catálogo é somente-leitura no local; create/update/delete seguem no Supabase.
 */
export async function getProducts<T = unknown>(
  restaurantId: string
): Promise<DataGatewayResult<T[]>> {
  return readWithFallback<T[]>(
    `/api/products?restaurant_id=${encodeURIComponent(restaurantId)}`,
    () =>
      supabase
        .from("products")
        .select("*, categories(name), production_locations(name, printer_name)")
        .eq("restaurant_id", restaurantId)
        .order("name", { ascending: true }) as unknown as Promise<{
        data: T[] | null;
        error: unknown;
      }>,
    (raw) => (Array.isArray(raw) ? (raw as T[]) : ([] as T[]))
  );
}

/**
 * commands — leitura (gêmeo de getTables). O hook re-ordena com sortByNumberField.
 */
export async function getCommands<T = unknown>(
  restaurantId: string
): Promise<DataGatewayResult<T[]>> {
  return readWithFallback<T[]>(
    `/api/commands?restaurant_id=${encodeURIComponent(restaurantId)}`,
    () =>
      supabase
        .from("commands")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .order("number") as unknown as Promise<{ data: T[] | null; error: unknown }>,
    (raw) => (Array.isArray(raw) ? (raw as T[]) : ([] as T[]))
  );
}

// Embed usado pelo useOrdersList (idêntico no local e na nuvem, p/ paridade de shape).
const ORDERS_LIST_EMBED = `
  *,
  items:order_items(id, product_id, product_name, quantity, unit_price, total_price, notes),
  payments:order_payments(id, amount, fee_amount, payment_method:payment_methods(id, name))`;

export interface OrdersListParams {
  restaurantId: string;
  status?: string; // undefined/"all" = sem filtro
  dateFrom?: string; // ISO
  dateTo?: string; // ISO (fim do dia)
  page: number; // base 1
  pageSize: number;
}

export interface OrdersListResult<T> {
  data: T[] | null;
  count: number;
  error: unknown;
  source: DataSource;
}

/**
 * orders (lista paginada com embeds + count) — leitura. Local: GET /api/orders ({data,count}).
 * Fallback: replica EXATAMENTE a query de nuvem do useOrdersList (mesmo embed, count exato,
 * order created_at desc, filtros status/data, range de paginação).
 */
export async function getOrdersList<T = unknown>(
  p: OrdersListParams
): Promise<OrdersListResult<T>> {
  if (!localLikelyDown()) {
    try {
      const qs = new URLSearchParams();
      qs.set("restaurant_id", p.restaurantId);
      if (p.status && p.status !== "all") qs.set("status", p.status);
      if (p.dateFrom) qs.set("date_from", p.dateFrom);
      if (p.dateTo) qs.set("date_to", p.dateTo);
      qs.set("page", String(p.page));
      qs.set("page_size", String(p.pageSize));
      const raw = await tryLocal<{ data: T[]; count: number }>(`/api/orders?${qs.toString()}`);
      markLocalUp();
      return { data: raw.data ?? [], count: raw.count ?? 0, error: null, source: emitSource("local") };
    } catch (e) {
      if (isBusinessError(e)) {
        return { data: null, count: 0, error: e.body, source: emitSource("local") };
      }
      markLocalDown();
    }
  }

  // fallback nuvem — idêntico ao comportamento atual do hook
  let query = supabase
    .from("orders")
    .select(ORDERS_LIST_EMBED, { count: "exact" })
    .eq("restaurant_id", p.restaurantId)
    .order("created_at", { ascending: false });
  if (p.status && p.status !== "all") query = query.eq("status", p.status as never);
  if (p.dateFrom) query = query.gte("created_at", p.dateFrom);
  if (p.dateTo) query = query.lte("created_at", p.dateTo);
  const from = (p.page - 1) * p.pageSize;
  query = query.range(from, from + p.pageSize - 1);

  const { data, error, count } = await query;
  return { data: data as unknown as T[] | null, count: count ?? 0, error, source: emitSource("cloud") };
}
